<script setup lang="ts">
// Below-the-fold stat counter: load it lazily so it stays out of the initial
// homepage bundle (it only animates once scrolled into view).
const NumberFlow = defineAsyncComponent(() => import('@number-flow/vue').then(m => m.default))

const statsRef = ref<HTMLElement>()
const yearsValue = ref(0)
const projectsValue = ref(0)

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          yearsValue.value = 24
          projectsValue.value = 150
          observer.disconnect()
        }
      })
    },
    { threshold: 0.3 }
  )

  if (statsRef.value) {
    observer.observe(statsRef.value)
  }
})
</script>

<template>
  <section id="about" class="py-20 md:py-40 relative">
    <div class="max-w-7xl mx-auto px-6 md:px-12">
      <div class="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
        <!-- Left: Content -->
        <div data-aos="fade">
          <div class="flex items-center gap-3 mb-6">
            <span class="text-xs tracking-[0.25em] text-blu-900 dark:text-blu-400 uppercase font-medium">About</span>
          </div>

          <h2 class="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-700 dark:text-white mb-6">
            Building software since 2001
          </h2>

          <p class="text-lg md:text-xl text-slate-700/80 dark:text-white/65 leading-relaxed text-pretty max-w-xl">
            Augment<span class="text-blu-600 dark:text-blu-400">BLU</span> is a web development studio with 24 years of building software for insurers, law firms, universities and government bodies. You deal with us directly, with no account managers in the way.
          </p>
        </div>

        <!-- Right: the figures and partner credits in an open grid, divided
             only by hairlines between them, on the same dot stage as the
             other sections. -->
        <VisualStage>
          <div class="relative overflow-hidden">
            <!-- auto-rows-fr: every row as tall as the tallest, so the four cells match. -->
            <div ref="statsRef" class="-mb-px -mr-px grid auto-rows-fr grid-cols-2">
              <div data-aos="fade" data-aos-delay="100" class="flex flex-col items-start justify-center gap-2 border-b border-r border-slate-900/10 p-6 dark:border-white/10 md:p-8">
                <div class="text-5xl md:text-6xl font-bold tracking-tight text-slate-700 dark:text-white"><NumberFlow :value="yearsValue" /></div>
                <div class="text-sm text-slate-700/75 dark:text-white/60">Years in development</div>
              </div>
              <div data-aos="fade" data-aos-delay="200" class="flex flex-col items-start justify-center gap-2 border-b border-r border-slate-900/10 p-6 dark:border-white/10 md:p-8">
                <div class="text-5xl md:text-6xl font-bold tracking-tight text-blu-600 dark:text-blu-400"><NumberFlow :value="projectsValue" /><span>+</span></div>
                <div class="text-sm text-slate-700/75 dark:text-white/60">Projects delivered</div>
              </div>
              <div data-aos="fade" data-aos-delay="300" class="flex items-center gap-4 border-b border-r border-slate-900/10 p-6 dark:border-white/10 md:p-8">
                <span class="flex h-12 w-12 shrink-0 items-center justify-center"><img src="/images/cms/modx.svg" alt="" width="44" height="44" class="h-11 w-auto" /></span>
                <div class="text-sm text-slate-700/75 dark:text-white/60">
                  <span class="block font-medium text-slate-700 dark:text-white/85">MODX</span>
                  Ambassador &amp; Partner<br>2013–2017
                </div>
              </div>
              <div data-aos="fade" data-aos-delay="400" class="flex items-center gap-4 border-b border-r border-slate-900/10 p-6 dark:border-white/10 md:p-8">
                <span class="flex h-12 w-12 shrink-0 items-center justify-center"><img src="/images/cms/october.svg" alt="" width="48" height="48" class="h-12 w-auto" /></span>
                <div class="text-sm text-slate-700/75 dark:text-white/60">
                  <span class="block font-medium text-slate-700 dark:text-white/85">OctoberCMS</span>
                  Partner<br>2015–2018
                </div>
              </div>
            </div>
          </div>
        </VisualStage>
      </div>
    </div>
  </section>
</template>
